import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import shopifyMark from '../assets/images/integrations/shopify-mark.svg';
import metaMark from '../assets/images/integrations/meta-mark.svg';
import klaviyoMark from '../assets/images/integrations/klaviyo-mark.svg';

const SOURCES_URL = 'https://api.amplifyloyalty.com/app/stores/analytics-sources';
const CACHE_TTL_MS = 5 * 60 * 1000;

export const ANALYTICS_SOURCE_LOGOS = {
  shopify: shopifyMark,
  'meta-ads': metaMark,
  klaviyo: klaviyoMark
};

/** Shown while loading and when the API call fails, so the Shopify page never loses its title. */
const FALLBACK_SOURCES = [{ id: 'shopify', name: 'Shopify', path: '/app/product-analysis/analytics' }];

// Module scope: Navigation and the page title share one request; a store switch reloads the page.
let cache = { value: null, fetchedAt: 0, promise: null };
const listeners = new Set();

function withLogos(sources) {
  return sources.map((s) => ({ ...s, logo: ANALYTICS_SOURCE_LOGOS[s.id] || null }));
}

/** Call after connecting / disconnecting an integration so switchers pick up the change. */
export function invalidateAnalyticsSources() {
  cache = { value: null, fetchedAt: 0, promise: null };
  listeners.forEach((fn) => fn());
}

async function loadSources(getJwtToken) {
  const fresh = cache.value && Date.now() - cache.fetchedAt < CACHE_TTL_MS;
  if (fresh) return cache.value;
  if (cache.promise) return cache.promise;
  cache.promise = (async () => {
    try {
      const token = await getJwtToken();
      const res = await axios.get(SOURCES_URL, { headers: { Authorization: `Bearer ${token}` } });
      const list = Array.isArray(res.data?.sources) ? res.data.sources : [];
      cache = { value: withLogos(list.length ? list : FALLBACK_SOURCES), fetchedAt: Date.now(), promise: null };
    } catch (err) {
      console.log(err);
      cache = { value: withLogos(FALLBACK_SOURCES), fetchedAt: Date.now(), promise: null };
    }
    return cache.value;
  })();
  return cache.promise;
}

/** Active analytics dashboards for the current store: `[{ id, name, path, logo }]`. */
export function useAnalyticsSources() {
  const { authed, getJwtToken } = useContext(AuthContext);
  const [sources, setSources] = useState(() => cache.value || withLogos(FALLBACK_SOURCES));
  const [loading, setLoading] = useState(!cache.value);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    listeners.add(bump);
    return () => listeners.delete(bump);
  }, []);

  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    loadSources(getJwtToken).then((list) => {
      if (cancelled) return;
      setSources(list);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [authed, getJwtToken, version]);

  return { sources, loading };
}
